import React, { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Key, Plus, Trash2, Copy, Check, Eye, EyeOff } from 'lucide-react'
import { useAlertStore } from '@/stores/stores'
import { AppShell, Topbar, Modal, ConfirmDialog, EmptyState, PageLoader, ToastContainer } from '../components/layout/Layout'
import { useForm } from 'react-hook-form'
import api from '../services/api'

const apiKeysApi = {
    list:   ()       => api.get('/admin/api-keys'),
    create: (data)   => api.post('/admin/api-keys', data),
    delete: (id)     => api.delete(`/admin/api-keys/${id}`),
}

export function ApiKeysPage() {
    const qc = useQueryClient()
    const { addToast } = useAlertStore()
    const [createOpen, setCreateOpen] = useState(false)
    const [deleteId, setDeleteId]     = useState(null)
    const [visibleKey, setVisibleKey] = useState(null)
    const [copied, setCopied]         = useState(null)
    const { register, handleSubmit, reset, watch } = useForm()

    const { data: keys = [], isLoading } = useQuery({
        queryKey: ['api-keys'],
        queryFn:  () => apiKeysApi.list().then(r => r.data),
    })
    const { data: screens = [] } = useQuery({
        queryKey: ['screens-ak'],
        queryFn:  () => api.get('/admin/screens').then(r => Array.isArray(r.data) ? r.data : r.data?.data ?? []),
    })
    const { data: users = [] } = useQuery({
        queryKey: ['users-ak'],
        queryFn:  () => api.get('/admin/users').then(r => Array.isArray(r.data) ? r.data : r.data?.data ?? []),
    })
    const scopeType = watch('scope_type', '')

    const createKey = async (data) => {
        try {
            await apiKeysApi.create(data)
            qc.invalidateQueries(['api-keys'])
            addToast({ type: 'success', title: 'API klíč vytvořen' })
            setCreateOpen(false)
            reset()
        } catch (e) {
            addToast({ type: 'error', title: 'Chyba vytvoření klíče' })
        }
    }

    const deleteKey = async (id) => {
        try {
            await apiKeysApi.delete(id)
            qc.invalidateQueries(['api-keys'])
            addToast({ type: 'success', title: 'API klíč smazán' })
            setDeleteId(null)
        } catch (e) {
            addToast({ type: 'error', title: 'Chyba mazání' })
        }
    }

    const copyKey = (key, id) => {
        navigator.clipboard.writeText(key)
        setCopied(id)
        setTimeout(() => setCopied(null), 2000)
        addToast({ type: 'success', title: 'Klíč zkopírován' })
    }

    return (
        <AppShell>
            <Topbar
                title="API klíče"
                subtitle="Správa přístupových klíčů pro externí systémy"
                actions={
                    <button className="btn btn-primary btn-sm" onClick={() => setCreateOpen(true)}>
                        <Plus size={14} /> Nový klíč
                    </button>
                }
            />
            <main className="app-main">
                {isLoading ? <PageLoader /> : keys.length > 0 ? (
                    <div className="card">
                        <div className="table-wrapper">
                            <table className="table">
                                <thead>
                                    <tr>
                                        <th>Název</th>
                                        <th>Klíč</th>
                                        <th>Poslední použití</th>
                                        <th>Vytvořen</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {keys.map(k => (
                                        <tr key={k.id}>
                                            <td style={{ fontWeight: 500 }}>{k.name}</td>
                                            <td>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'var(--font-mono)', fontSize: '0.8rem' }}>
                                                    {visibleKey === k.id ? k.key : '••••••••••••••••'}
                                                    <button className="btn btn-ghost btn-sm btn-icon"
                                                        onClick={() => setVisibleKey(visibleKey === k.id ? null : k.id)}>
                                                        {visibleKey === k.id ? <EyeOff size={12} /> : <Eye size={12} />}
                                                    </button>
                                                    <button className="btn btn-ghost btn-sm btn-icon"
                                                        onClick={() => copyKey(k.key, k.id)}>
                                                        {copied === k.id ? <Check size={12} color="var(--green)" /> : <Copy size={12} />}
                                                    </button>
                                                </div>
                                            </td>
                                            <td style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                                                {k.scope_type ? `${k.scope_type}` : 'celý tenant'}
                                            </td>
                                            <td style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                                                {k.last_used_at ?? '—'}
                                            </td>
                                            <td style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                                                {k.created_at ? new Date(k.created_at).toLocaleDateString('cs-CZ') : '—'}
                                            </td>
                                            <td>
                                                <button className="btn btn-ghost btn-sm btn-icon"
                                                    onClick={() => setDeleteId(k.id)}
                                                    style={{ color: 'var(--red)' }}>
                                                    <Trash2 size={14} />
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <EmptyState
                        icon={Key}
                        title="Žádné API klíče"
                        text="Vytvořte API klíč pro přístup externích systémů (ERP, POS...)"
                        action={
                            <button className="btn btn-primary" onClick={() => setCreateOpen(true)}>
                                <Plus size={14} /> Vytvořit klíč
                            </button>
                        }
                    />
                )}
            </main>

            <Modal
                open={createOpen}
                onClose={() => setCreateOpen(false)}
                title="Nový API klíč"
                footer={
                    <>
                        <button className="btn btn-secondary" onClick={() => setCreateOpen(false)}>Zrušit</button>
                        <button className="btn btn-primary" onClick={handleSubmit(createKey)}>Vytvořit</button>
                    </>
                }
            >
                <div className="form-group">
                    <label className="form-label">Název klíče</label>
                    <input {...register('name', { required: true })} className="form-input"
                        placeholder="ERP systém, POS pokladna..." autoFocus />
                </div>
                <div className="form-group">
                    <label className="form-label">Popis (volitelný)</label>
                    <input {...register('description')} className="form-input"
                        placeholder="K čemu se klíč používá..." />
                </div>
                <div className="form-group">
                    <label className="form-label">Rozsah přístupu</label>
                    <select {...register('scope_type')} className="form-select">
                        <option value="">Celý tenant (výchozí)</option>
                        <option value="screens">Konkrétní obrazovky</option>
                        <option value="user">Konkrétní uživatel</option>
                    </select>
                </div>
                {scopeType === 'screens' && (
                    <div className="form-group">
                        <label className="form-label">Obrazovky</label>
                        <select multiple {...register('scope_ids')} className="form-select" size={4}>
                            {screens.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                        <div className="form-hint">Podržte Ctrl pro výběr více obrazovek</div>
                    </div>
                )}
                {scopeType === 'user' && (
                    <div className="form-group">
                        <label className="form-label">Uživatel</label>
                        <select {...register('scope_ids')} className="form-select">
                            <option value="">-- Vyberte uživatele --</option>
                            {users.map(u => (
                                <option key={u.id} value={u.id}>{u.name} ({u.email})</option>
                            ))}
                        </select>
                    </div>
                )}
                <div className="form-row form-row-2">
                    <div className="form-group">
                        <label className="form-label">Rate limit (req/min)</label>
                        <input {...register('rate_limit')} type="number" className="form-input"
                            placeholder="60" min="1" max="1000" />
                    </div>
                    <div className="form-group">
                        <label className="form-label">Platnost do</label>
                        <input {...register('expires_at')} type="date" className="form-input" />
                    </div>
                </div>
            </Modal>

            <ConfirmDialog
                open={!!deleteId}
                onClose={() => setDeleteId(null)}
                onConfirm={() => deleteKey(deleteId)}
                title="Smazat API klíč"
                message="Klíč bude trvale smazán. Všechny integrace používající tento klíč přestanou fungovat."
                danger
            />
            <ToastContainer />
        </AppShell>
    )
}
